import { Check, Minus } from "lucide-react";

import type { AuditLogEntry, JobStatus } from "@/api/types";
import { cn } from "@/lib/utils";

/**
 * Live mode-progress panel for curator jobs — three pills above the
 * summary card on JobDetail (issue
 * [#240](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/240)).
 * Each pill represents one of the curator's three discovery modes and
 * renders one of four states: `queued` (not yet mentioned in any
 * event), `running` (the agent is in the mode now), `complete` (the
 * agent finished or skipped it), or `abandoned` (the agent entered
 * the mode but the job reached a terminal state before a `complete`
 * event arrived — issue
 * [#637](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/637)).
 *
 * **Single source of truth.** State is derived from `audit_log` rows of
 * type `mode_progress_recorded` — written by the orchestrator's
 * dispatch-runner each time it forwards a `mode_progress` SSE event
 * from the agent. Live updates come for free: `useRunStream` triggers
 * a `useJob` refetch on every SSE event, the refetch pulls the new
 * audit row, this component re-renders. Replay on mid-run page reload
 * and on terminal jobs comes for free too — the snapshot route's
 * `audit` array already includes these rows.
 *
 * **Terminal override.** When `jobStatus` is one of `completed`,
 * `failed`, `expired`, or `cancelled`, any pill whose last audited
 * status was `running` collapses to `abandoned` — the LLM either
 * decided to skip the closing `record_mode_progress(*, complete)` call
 * or the process exited mid-mode, so leaving the pill as live-running
 * misrepresents a terminal job. The `abandoned` visual is deliberately
 * distinct from `complete` so an operator can still tell "LLM said it
 * finished" apart from "LLM never reported finishing."
 *
 * Curator-only: callers gate on `agentType === "db_curator"`. The
 * validator strand and any future non-curator agent have no mode
 * vocabulary today; the schema's `mode` field is free text so a
 * future agent could emit a different set, in which case the
 * {@link MODE_PILLS} display table below is the single load-bearing
 * piece to extend.
 */

/** Per-mode UI state computed from the audit_log mode_progress rows
 * and (for `abandoned`) the containing job's terminal status. */
export type ModeStatus = "queued" | "running" | "complete" | "abandoned";

/** Terminal `JobStatus` values that trigger the `running → abandoned`
 * override in {@link extractModeProgress}. Mirrors the platform state
 * machine's terminal set — exported as a runtime constant so tests
 * loop over the same set the reducer consumes, closing the drift risk
 * of a hand-copied literal list. */
export const TERMINAL_JOB_STATUSES: ReadonlySet<JobStatus> = new Set<JobStatus>([
  "completed",
  "failed",
  "expired",
  "cancelled",
]);

/** The curator's three modes — kept in sync with the
 * `curatorArtifactSchema.modesAttempted` enum in
 * `packages/shared/src/schemas/curator-artifacts.ts` and the prompt
 * vocabulary in `packages/prompts/src/db_curator/autonomous.md`. */
const CURATOR_MODES = ["mode_1", "mode_2", "mode_3"] as const;
type CuratorMode = (typeof CURATOR_MODES)[number];

/**
 * Display table for the pills — pairs each mode key with its operator-
 * facing label. Labels mirror the prose in the curator's system prompt
 * (catalogue review = Mode 1's autonomous web search, gap analysis =
 * Mode 2's source monitoring, deep dive = Mode 3's stale-record
 * refresh) so an operator scanning the pills sees the same names as
 * the prompt-level documentation.
 *
 * Exported for unit testing.
 */
export const MODE_PILLS: ReadonlyArray<{ key: CuratorMode; label: string }> = [
  { key: "mode_1", label: "Mode 1: catalogue review" },
  { key: "mode_2", label: "Mode 2: gap analysis" },
  { key: "mode_3", label: "Mode 3: deep dive" },
];

/**
 * Reduce `audit_log` entries into a per-mode status map. The audit_log
 * is sorted chronologically by the orchestrator's stream route (see
 * `loadSnapshot` in `services/orchestrator/src/routes/streams.ts`), so
 * walking it in order and overwriting each mode's last-seen status
 * yields the right terminal state: `running` (mode in progress when
 * the run ended/was interrupted) sticks if no `complete` follows.
 *
 * When `jobStatus` is terminal (`completed | failed | expired |
 * cancelled`), any pill still on `running` is rewritten to `abandoned`
 * — the containing job is no longer live, so "RUNNING" would be a
 * lie. `queued` pills stay `queued` (that mode was never entered, and
 * the queued visual on a terminal job accurately reports "never
 * started"). See issue #637 for the reproducer.
 *
 * Anything outside the known three modes is silently dropped — the
 * pills only render the curator-specific vocabulary. The payload is
 * narrowed defensively because audit_log payloads are typed `unknown`
 * on the SPA side.
 *
 * Exported for unit testing.
 */
export function extractModeProgress(
  auditLog: ReadonlyArray<AuditLogEntry>,
  jobStatus?: JobStatus,
): Record<CuratorMode, ModeStatus> {
  const out: Record<CuratorMode, ModeStatus> = {
    mode_1: "queued",
    mode_2: "queued",
    mode_3: "queued",
  };
  for (const entry of auditLog) {
    if (entry.eventType !== "mode_progress_recorded") continue;
    const payload = entry.payload as
      | { mode?: unknown; status?: unknown }
      | null;
    if (!payload || typeof payload !== "object") continue;
    const mode = payload.mode;
    const status = payload.status;
    if (!isCuratorMode(mode)) continue;
    if (status !== "running" && status !== "complete") continue;
    out[mode] = status;
  }
  if (jobStatus && TERMINAL_JOB_STATUSES.has(jobStatus)) {
    for (const mode of CURATOR_MODES) {
      if (out[mode] === "running") out[mode] = "abandoned";
    }
  }
  return out;
}

function isCuratorMode(value: unknown): value is CuratorMode {
  return (
    value === "mode_1" || value === "mode_2" || value === "mode_3"
  );
}

/**
 * Three-pill panel rendered above the JobSummaryCard on curator jobs.
 * The visual contract per state:
 *
 * - `complete` → emerald border + leading check icon (mirrors the
 *   `success` badge variant at `ui/badge.tsx`).
 * - `running` → accent border + leading pulsing pip (mirrors the
 *   `LiveIndicator` at JobDetail.tsx).
 * - `queued` → muted border + leading hollow ring.
 * - `abandoned` → amber-tinged muted border + leading minus glyph +
 *   " · incomplete" suffix. Deliberately distinct from `complete` so
 *   the operator can distinguish "LLM said it finished" from "LLM
 *   never reported finishing" on a terminal job (issue #637).
 *
 * `jobStatus` is optional so the component still works on live-run
 * paths where the wrapper hasn't threaded it through; when absent, no
 * terminal override fires and pills stay on their last audited state
 * (the pre-#637 behaviour, still correct for live jobs).
 */
export function ModeProgressPills({
  auditLog,
  jobStatus,
}: {
  auditLog: ReadonlyArray<AuditLogEntry>;
  jobStatus?: JobStatus;
}) {
  const states = extractModeProgress(auditLog, jobStatus);
  return (
    <section aria-label="Mode progress" className="flex flex-wrap gap-2">
      {MODE_PILLS.map(({ key, label }) => (
        <ModePill key={key} label={label} status={states[key]} />
      ))}
    </section>
  );
}

function ModePill({ label, status }: { label: string; status: ModeStatus }) {
  return (
    <span
      data-status={status}
      className={cn(
        "inline-flex items-center gap-2 rounded border px-3 py-1 text-xs font-mono",
        status === "complete" &&
          "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
        status === "running" && "border-accent/40 bg-accent-dim text-accent",
        status === "queued" && "border-border bg-bg-card text-text-faint",
        status === "abandoned" &&
          "border-amber-500/30 bg-amber-500/5 text-amber-300/80",
      )}
    >
      <StatusGlyph status={status} />
      <span>{label}</span>
      {status === "running" ? (
        <span className="uppercase tracking-label">· running</span>
      ) : null}
      {status === "abandoned" ? (
        <span className="uppercase tracking-label">· incomplete</span>
      ) : null}
    </span>
  );
}

function StatusGlyph({ status }: { status: ModeStatus }) {
  if (status === "complete") {
    return <Check className="h-3.5 w-3.5" aria-hidden />;
  }
  if (status === "running") {
    return (
      <span
        className="h-2 w-2 rounded-full bg-accent animate-pulse"
        aria-hidden
      />
    );
  }
  if (status === "abandoned") {
    return <Minus className="h-3.5 w-3.5" aria-hidden />;
  }
  return (
    <span
      className="h-2 w-2 rounded-full border border-text-faint/60"
      aria-hidden
    />
  );
}
